// pages/sections/ClientDataBulkModal.js
// Bulk edit: one set of changes applied to every selected client. Every field
// starts at "No change" and only what you set is sent. DI never writes Practice
// Engine, so the result is recorded in DI and flagged "pending PE". The server
// judges each client on its own (your role on it, sign-off lock, Partner-only
// Manager change) and reports back what it skipped.

import React, { useMemo, useState } from 'react';
import Modal from '../../components/Modal';
import { FLAGS } from './clientDataUtil';

const BOOL_FIELDS = [
  ...FLAGS.map(f => ({ key: f.prop, label: f.title, help: f.help, diOnly: f.diOnly })),
  { key: 'isReferral', label: 'Referral source' },
];

// canEditManager (also gates the Partner field): an admin, or the Partner on at least one selected client.
export default function ClientDataBulkModal({ count, employees, saving, canEditManager, adminView, onCancel, onSave }) {
  const [bools, setBools]       = useState(() => Object.fromEntries(BOOL_FIELDS.map(f => [f.key, 'keep'])));
  const [fe, setFe]             = useState('keep');           // keep | none | fam | emp
  const [empEmail, setEmpEmail] = useState('');
  const [managerEmail, setManagerEmail] = useState('');       // '' = no change
  const [partnerEmail, setPartnerEmail] = useState('');       // '' = no change

  const changes = useMemo(() => {
    const c = {};
    for (const f of BOOL_FIELDS) if (bools[f.key] !== 'keep') c[f.key] = bools[f.key] === 'yes';
    if (fe !== 'keep') c.famEmp = { type: fe, employeeEmail: fe === 'emp' ? empEmail : undefined };
    if (canEditManager && managerEmail) c.managerEmail = managerEmail;
    if (canEditManager && partnerEmail) c.partnerEmail = partnerEmail;
    return c;
  }, [bools, fe, empEmail, managerEmail, partnerEmail, canEditManager]);

  const dirty = Object.keys(changes).length > 0;
  const empMissing = fe === 'emp' && !empEmail;

  return (
    <Modal
      open
      onClose={saving ? () => {} : onCancel}
      title={`Bulk edit — ${count} client${count === 1 ? '' : 's'}`}
      footer={
        <>
          <button type="button" className="cd-btn cd-btn--ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="button" className="cd-btn cd-btn--primary" disabled={!dirty || empMissing || saving} onClick={() => onSave(changes)}>
            {saving ? 'Applying…' : `Apply to ${count} client${count === 1 ? '' : 's'}`}
          </button>
        </>
      }
    >
      <p className="cd-modal-note">
        Anything left on <strong>No change</strong> is untouched. Changes are recorded in Data Integrity and marked{' '}
        <strong>pending PE</strong> until they&apos;re applied in Practice Engine.
      </p>

      <div className="cd-form">
        {BOOL_FIELDS.map(f => (
          <label key={f.key} className="cd-field cd-field--row" title={f.help}>
            <span>{f.label}{f.diOnly && <span className="cd-static-lock" aria-hidden="true">Not sent to PE</span>}</span>
            <select className="cd-select" value={bools[f.key]} onChange={e => setBools(b => ({ ...b, [f.key]: e.target.value }))}>
              <option value="keep">No change</option>
              <option value="yes">Set to Yes</option>
              <option value="no">Set to No</option>
            </select>
          </label>
        ))}

        <label className="cd-field cd-field--row">
          <span>FAM / EMP</span>
          <select className="cd-select" value={fe} onChange={e => setFe(e.target.value)}>
            <option value="keep">No change</option>
            <option value="none">Neither</option>
            <option value="fam">Friend &amp; Family</option>
            <option value="emp">Employee</option>
          </select>
        </label>
        {fe === 'emp' && (
          <label className="cd-field cd-field--row">
            <span>Employee</span>
            <select className="cd-select" value={empEmail} onChange={e => setEmpEmail(e.target.value)}>
              <option value="">— Select the employee —</option>
              {employees.map(e => <option key={e.email} value={e.email}>{e.name}</option>)}
            </select>
          </label>
        )}

        {canEditManager ? (
          <label className="cd-field cd-field--row">
            <span>Client Manager</span>
            <select className="cd-select" value={managerEmail} onChange={e => setManagerEmail(e.target.value)}>
              <option value="">No change</option>
              {employees.map(e => <option key={e.email} value={e.email}>{e.name}</option>)}
            </select>
          </label>
        ) : (
          <div className="cd-field cd-field--row">
            <span>Client Manager</span>
            <div className="cd-static" title="Only a client's Partner can change its Manager">
              No change<span className="cd-static-lock" aria-hidden="true">Partner only</span>
            </div>
          </div>
        )}
        {canEditManager ? (
          <label className="cd-field cd-field--row">
            <span>Client Partner</span>
            <select className="cd-select" value={partnerEmail} onChange={e => setPartnerEmail(e.target.value)}>
              <option value="">No change</option>
              {employees.map(e => <option key={e.email} value={e.email}>{e.name}</option>)}
            </select>
          </label>
        ) : (
          <div className="cd-field cd-field--row">
            <span>Client Partner</span>
            <div className="cd-static" title="Only a client's Partner can change its Partner">
              No change<span className="cd-static-lock" aria-hidden="true">Partner only</span>
            </div>
          </div>
        )}

        {(managerEmail || partnerEmail) && !adminView && (
          <p className="cd-warn">
            The Manager / Partner change only applies to clients where you are the Partner; any others are skipped.
          </p>
        )}
        {managerEmail && (
          <p className="cd-warn">
            Reassigning the Manager moves each client to their list and takes it off the current Manager&apos;s.
          </p>
        )}
        {partnerEmail && (
          <p className="cd-warn">
            Reassigning the Partner moves each client to their list and takes it off the current Partner&apos;s.
          </p>
        )}
      </div>
    </Modal>
  );
}
